import React, { useState } from "react";
import { withStyles } from "@material-ui/core/styles";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContentText,
  DialogActions,
  DialogContent,
  CircularProgress,
} from "@material-ui/core";
import _ from "lodash";
import * as Icons from "@material-ui/icons";
import { useSelector, shallowEqual } from "react-redux";
import { useHistory } from "react-router-dom";
import { useQuery, useLazyQuery, useMutation } from "@apollo/client";
import { DataGrid } from "@material-ui/data-grid";

import { Graph } from "./partials/graph";
import { PreparationFilter } from "./partials/preparationFilter";
import { CustomSlider } from "./partials/slider";
import {
  GET_MATCHING_CAREERS,
  GET_OCCUPATIONS,
  RESET_QUIZ_PROGRESS,
} from "../_redux/quizCrud";

import { useDispatch } from "react-redux";

import { quizResultsSlice } from "../../quiz/_redux/quizSlice";

const { actions } = quizResultsSlice;

export function QuizResults() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { quizResults } = useSelector((state) => state, shallowEqual);
  const { user } = useSelector((state) => state.auth);

  const [resetQuiz, { loading: resetLoading }] = useMutation(
    RESET_QUIZ_PROGRESS
  );

  function getPreparationImageSrc(level) {
    switch (level) {
      case 1:
        return "/media/svg/preparation-needed/1.svg";
      case 2:
        return "/media/svg/preparation-needed/2.svg";
      case 3:
        return "/media/svg/preparation-needed/3.svg";
      case 4:
        return "/media/svg/preparation-needed/4.svg";
      case 5:
        return "/media/svg/preparation-needed/5.svg";

      default:
        return "";
    }
  }

  const columns = [
    {
      field: "occupationTitle",
      headerName: "Occupation",
      width: 450,
      renderCell: (params) => {
        return (
          <Box style={{ cursor: "pointer" }}>
            <a
              href={`${process.env.REACT_APP_CAREER_BROWSER_DASH_URL}/#/occupation/${params.row.industryId}/${params.row.occupationId}`}
            >
              <Typography color="secondary"> {params.value}</Typography>
            </a>
          </Box>
        );
      },
    },
    {
      field: "jobZoneId",
      headerName: "Preparation needed",
      width: 200,
      renderCell: (params) => {
        if (!params.value) {
          return "N/A";
        }
        return (
          <img
            width={30}
            height={25}
            src={getPreparationImageSrc(params.value)}
            alt={params.value}
          />
        );
      },
    },
    {
      field: "annualMedianWage",
      headerName: "Annual Median wage",
      width: 200,
      renderCell: (params) => {
        if (!params.value) {
          return "N/A";
        }
        return "$" + params.value;
      },
    },
    {
      field: "industryTitle",
      headerName: "Industry Name",
      width: 350,
    },
  ];

  const definitions = {
    Realistic: {
      title: "Realistic",
      description:
        "Realistic occupations frequently involve work activities that include practical, hands-on problems and solutions. They often deal with plants, animals, and real-world materials like wood, tools, and machinery. Many of the occupations require working outside, and do not involve a lot of paperwork or working closely with others.",
      color: "rgba(13, 172, 80,1)",
    },
    Investigative: {
      title: "Investigative",
      description:
        "Investigative occupations frequently involve working with ideas, and requirean extensive amount of thinking. These occupations can involve searching for facts and figuring out problems mentally.",
      color: "rgba(60, 115, 168,1)",
    },
    Artistic: {
      title: "Artistic",
      description:
        "Artistic occupations frequently involve working with forms, designs and patterns. They often require self-expression and the work can be done without following a clear set of rules.",
      color: "rgba(231, 76, 60, 1)",
    },
    Social: {
      title: "Social",
      description:
        "Social occupations frequently involve working with, communicating with, and teaching people. These occupations often involve helping or providing service to others.",
      color: "rgba(154, 18, 179,1)",
    },
    Enterprising: {
      title: "Enterprising",
      description:
        "Enterprising occupations frequently involve starting up and carrying out projects. These occupations can involve leading people and making many decisions. Sometimes they require risk taking and often deal with business.",
      color: "rgba(255, 205, 2, 1)",
    },
    Conventional: {
      title: "Conventional",
      description:
        "Conventional occupations frequently involve following set procedures and routines. These occupations can include working with data and details more than with ideas. Usually there is a clear line of authority to follow.",
      color: "rgba(243, 156, 18,1)",
    },
  };

  const { loading } = useQuery(GET_MATCHING_CAREERS, {
    variables: {
      matchingCareersAnswers: quizResults.answers,
    },
    onCompleted: (data) => {
      const occupationIds = data.matchingCareers.career.map(
        (occupation) => occupation.code
      );
      getOccupations({
        variables: { getOccupationsOccupationIds: occupationIds },
      });
    },
  });

  const [getOccupations, { loading: occupationsLoading }] = useLazyQuery(
    GET_OCCUPATIONS,
    {
      onCompleted: (res) => {
        console.log(res);
        let finalResult = [];
        res.getOccupations.occupations.forEach((occupation) => {
          const industriesForOccupation = _.filter(
            res.getOccupations.occupationAnnualWageMap,
            { blsOccupationId: occupation.blsOccupationId }
          );
          industriesForOccupation.forEach((oc) => {
            const industryDetails = _.find(res.getOccupations.industryList, {
              blsIndustryId: oc.blsIndustryId,
            });
            finalResult.push({
              industryTitle: industryDetails.title,
              industryId: industryDetails.blsIndustryId,
              occupationTitle: occupation.title,
              occupationId: occupation.occupationId,
              jobZoneId: occupation.jobZoneId,
              id: industryDetails.blsIndustryId + occupation.occupationId,
              annualMedianWage: oc.value,
            });
          });
        });
        let occupationList = res.getOccupations.occupationAnnualWageMap.map(
          (occupation) => {
            const industry = _.find(res.getOccupations.industryList, {
              blsIndustryId: occupation.blsIndustryId,
            });

            const occupationDetails = _.find(res.getOccupations.occupations, {
              blsOccupationId: occupation.blsOccupationId,
            });
            return {
              industryTitle: industry.title,
              industryId: industry.blsIndustryId,
              occupationTitle: occupationDetails.title,
              occupationId: occupationDetails.occupationId,
              jobZoneId: occupationDetails.jobZoneId,
              id: occupation.blsIndustryId + occupationDetails.occupationId,
              annualMedianWage: occupation.value,
            };
          }
        );
        const duplicatesTemp = {};
        const occupations = occupationList.filter((o) => {
          if (
            Object.keys(duplicatesTemp).includes(o.industryId + o.occupationId)
          ) {
            return false;
          } else {
            duplicatesTemp[o.industryId + o.occupationId] = 0;
            return true;
          }
        });
        setOccupations(occupations);
        setQueryResult(occupations);
      },
    }
  );

  const rows = _.orderBy(quizResults.results, ["score"], ["desc"]);
  if (!rows.length) {
    history.push("/");
  }

  const [preparationLevel, setPreparationLevel] = useState([]);
  const [minimumWageValue, setMinimumWageValue] = useState(0);
  const [queryResult, setQueryResult] = useState(undefined);
  const [occupations, setOccupations] = useState([]);
  const [open, setOpen] = useState(false);

  const colors = [
    "rgba(231, 76, 60, 1)",
    "rgba(60, 115, 168,1)",
    "rgba(255, 205, 2, 1)",
    "rgba(13, 172, 80,1)",
    "rgba(154, 18, 179,1)",
    "rgba(243, 156, 18,1)",
  ];

  function changePreparationLevel(level) {
    let levels = [];
    levels = preparationLevel;
    if (preparationLevel.includes(level)) {
      levels.splice(levels.indexOf(level), 1);
    } else {
      levels.push(level);
    }
    let filteredOccupations = queryResult;

    if (levels.length) {
      filteredOccupations = queryResult.filter((occupation) =>
        levels.includes(occupation.jobZoneId)
      );
    }
    if (minimumWageValue !== 0) {
      filteredOccupations = filteredOccupations.filter(
        (occupation) => occupation.annualMedianWage >= minimumWageValue
      );
    }
    setOccupations(filteredOccupations);
    setPreparationLevel(levels);
  }

  const HtmlTooltip = withStyles((theme) => ({
    tooltip: {
      backgroundColor: "#01a9f4",
      color: "white",
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(16),
      border: "1px solid #dadde9",
    },
  }))(Tooltip);

  const handleSliderChange = (event, newValue) => {
    setMinimumWageValue(newValue);
    let filteredOccupations = queryResult;
    if (preparationLevel.length) {
      filteredOccupations = filteredOccupations.filter((occupation) =>
        preparationLevel.includes(occupation.jobZoneId)
      );
    }
    filteredOccupations = queryResult.filter(
      (occupation) => occupation.annualMedianWage >= newValue
    );
    setOccupations(filteredOccupations);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const retakeQuiz = async () => {
    if (user) {
      await resetQuiz();
    }
    dispatch(actions.resetAnswers());
    history.push("/");
  };

  return (
    <Box>
      <Dialog
        onClose={handleClose}
        aria-labelledby="simple-dialog-title"
        keepMounted
        open={open}
      >
        <DialogTitle id="simple-dialog-title">
          Please confirm to retake questionaire
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            If you click in continue then your current results will be lost.
            Please confirm.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={retakeQuiz}
            style={{ color: "#3699FF", borderColor: "#3699FF" }}
            disabled={resetLoading}
          >
            {resetLoading ? (
              <CircularProgress style={{ color: "#3699FF" }} color="#3699FF" />
            ) : (
              "Retake"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Box>
        <Paper>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            textAlign="center"
            p={5}
          >
            <Typography variant="h2">
              <b>Your Results</b>
            </Typography>
            <Typography variant="h6">
              Think of your intersts as work you like to do.
            </Typography>
            <Typography variant="h6">
              Your intersts can help you find careers you might like to explore.
              The more a career meets your intersts, the more likely it will be
              satisfying and rewarding to you.
            </Typography>
          </Box>

          <Grid container alignItems="center" justify="center">
            <Grid item xs={12} md={6}>
              <Graph graphData={rows} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box m={2} mr={5}>
                <TableContainer>
                  <Table aria-label="results">
                    <TableBody>
                      {rows.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell
                            component="th"
                            scope="row"
                            style={{ fontSize: 15, borderBottom: "none" }}
                            align="right"
                          >
                            {index + 1}
                          </TableCell>
                          <TableCell style={{ borderBottom: "none" }}>
                            {index > 2 ? (
                              <Typography
                                style={{
                                  fontSize: 15,
                                  color: colors[index],
                                }}
                              >
                                {row.area}
                                <HtmlTooltip
                                  title={
                                    definitions[row.area].title +
                                    "--" +
                                    definitions[row.area].description
                                  }
                                  arrow
                                >
                                  <IconButton aria-label="delete" size="medium">
                                    <Icons.InfoRounded fontSize="inherit" />
                                  </IconButton>
                                </HtmlTooltip>
                              </Typography>
                            ) : (
                              <Typography
                                style={{
                                  fontSize: 15,
                                  color: colors[index],
                                }}
                              >
                                <b>{row.area}</b>
                                <HtmlTooltip
                                  title={
                                    definitions[row.area].title +
                                    " -- " +
                                    definitions[row.area].description
                                  }
                                  arrow
                                >
                                  <IconButton aria-label="delete" size="medium">
                                    <Icons.InfoRounded fontSize="inherit" />
                                  </IconButton>
                                </HtmlTooltip>
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell
                            style={{
                              fontSize: 15,
                              borderBottom: "none",
                              color: definitions[row.area].color,
                            }}
                            align="right"
                          >
                            {index > 2 ? row.score : <b>{row.score}</b>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Grid>
          </Grid>
          <Box textAlign="center">
            <Button
              style={{ color: "#3699FF", borderColor: "#3699FF" }}
              size="large"
              variant="outlined"
              onClick={() => {
                handleOpen();
              }}
            >
              Retake Questionaire
            </Button>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            textAlign="center"
          >
            <Box display="flex" mt={5}>
              <Box>
                <Typography variant="h2">
                  <b>MATCHING OCCUPATIONS</b>
                </Typography>
                <Typography>
                  ({occupations.length ? occupations.length : "-"}) Occupations
                  match your interest profile
                </Typography>
              </Box>
              <Box
                style={{ backgroundColor: "#01a9f4", borderRadius: "50px" }}
                p={2}
                ml={3}
              >
                <Icons.BusinessCenterOutlined
                  style={{ fontSize: 75, color: "white" }}
                />
              </Box>
            </Box>
            <Box display="flex" width="60%" mt={3} mb={3}>
              <Icons.MonetizationOn
                fontSize="large"
                style={{ color: "#01a9f4" }}
              />
              <CustomSlider
                min={
                  queryResult
                    ? _.min(queryResult.map((o) => o.annualMedianWage))
                    : 0
                }
                max={
                  queryResult
                    ? _.max(queryResult.map((o) => o.annualMedianWage))
                    : 100
                }
                onChange={queryResult ? handleSliderChange : () => {}}
                step={5000}
                // reset
              />
            </Box>
            <Box>
              <PreparationFilter
                changePreparationLevel={
                  queryResult ? changePreparationLevel : () => {}
                }
                preparationLevel={preparationLevel}
              />
            </Box>
            <Box height={700} width={1} p={5}>
              <DataGrid
                hideFooter
                hideFooterPagination
                rows={occupations}
                columns={columns}
                loading={loading || occupationsLoading}
              />
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
